/**
 * OrgReportsTabContent
 *
 * Organization-level standard reports page. Mirrors the ReportsTab UI but
 * operates at organization scope: data is aggregated across all org workspaces
 * and a Workspace column is shown on each report. SOS Report is included as a
 * 6th standard tab using the existing OrgReportsTab component.
 */

import React, { useState } from 'react';
import {
  Users,
  FileText,
  Flame,
  UserCheck,
  Vote,
  Trophy,
  ClipboardCheck,
  Newspaper,
} from 'lucide-react';
import BriefsReport from '../reports/BriefsReport';
import HotBillsReport from '../reports/HotBillsReport';
import LegislatorVotesReportWrapper from '../reports/LegislatorVotesReportWrapper';
import BillTrackingReport from '../reports/BillTrackingReport';
import LegislatorsReport from '../reports/LegislatorsReport';
import CandidatesReport from '../reports/CandidatesReport';
import BallotInitiativesReport from '../reports/BallotInitiativesReport';
import ScorecardReport from '../reports/ScorecardReport';
import OrgReportsTab from '../organization-settings/OrgReportsTab';
import ShareUrlDisplay from '../reports/ShareUrlDisplay';
import { HelpTrigger } from '../../help';
import { isShareableStandardReportType } from '../../types/reports';

export type OrgReportType =
  | 'briefs'
  | 'hot-bills'
  | 'bill-tracking'
  | 'legislators'
  | 'candidates'
  | 'ballot-initiatives'
  | 'legislator-votes-on-tracked-bills'
  | 'scorecard'
  | 'sos-report';

const ORG_STANDARD_REPORTS: Array<{
  id: OrgReportType;
  title: string;
  /** Shorter tile text; the tile shows the full title when absent. */
  tileLabel?: string;
  description: string;
  icon: React.ElementType;
  color: string;
}> = [
  {
    id: 'briefs',
    title: 'Briefs',
    description: 'Date-ranged client & portfolio briefs across selected workspaces',
    icon: Newspaper,
    color: 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300',
  },
  {
    id: 'hot-bills',
    title: 'Hot Bills Report',
    tileLabel: 'Hot Bills',
    description: 'Priority bills with calendar events across all workspaces',
    icon: Flame,
    color: 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-300',
  },
  {
    id: 'bill-tracking',
    title: 'Bills',
    description: 'Bill tracking across all workspaces with workspace column',
    icon: FileText,
    color: 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300',
  },
  {
    id: 'legislators',
    title: 'Legislators',
    description: 'Tracked legislators across all workspaces',
    icon: Users,
    color: 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-300',
  },
  {
    id: 'candidates',
    title: 'Candidates',
    description: 'Tracked candidates across all workspaces',
    icon: UserCheck,
    color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  },
  {
    id: 'ballot-initiatives',
    title: 'Ballot Initiatives',
    description: 'Ballot measures tracked across all workspaces, as citizens see them',
    icon: Vote,
    color: 'bg-info-100 dark:bg-info-900/20 text-info-700 dark:text-info-300',
  },
  {
    id: 'legislator-votes-on-tracked-bills',
    title: 'Votes',
    description: 'How tracked legislators voted on tracked bills, org-wide',
    icon: UserCheck,
    color: 'bg-info-100 dark:bg-info-900/20 text-info-700 dark:text-info-300',
  },
  {
    id: 'scorecard',
    title: 'Scorecard',
    description: 'Legislator voting alignment scores across all workspaces',
    icon: Trophy,
    color: 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300',
  },
  {
    id: 'sos-report',
    title: 'SOS Report',
    description: 'Secretary of State position tracking report',
    icon: ClipboardCheck,
    color: 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary',
  },
];

interface OrgReportsTabContentProps {
  organizationId: string;
  organizationName?: string;
  organizationLogoUrl?: string | null;
}

const OrgReportsTabContent: React.FC<OrgReportsTabContentProps> = ({
  organizationId,
  organizationName = 'Organization',
  organizationLogoUrl,
}) => {
  const [selectedReportType, setSelectedReportType] = useState<OrgReportType>('briefs');

  const isSosReport = selectedReportType === 'sos-report';
  // Briefs have no public share link yet (PRD O5 deferred) — exclude explicitly.
  const isShareableStandardReport =
    selectedReportType !== 'briefs' && isShareableStandardReportType(selectedReportType);

  return (
    <div className="space-y-4">
      {/* Header */}
      <h2 className="text-xl font-bold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
        Standard Organization Reports
        <span className="hidden md:inline-flex">
          <HelpTrigger contentKey="workspace-reports" size="md" />
        </span>
      </h2>

      {/* Report Selection */}
      <div className="space-y-3">
        {/* Compact Report Selection - mirrors ReportsTab */}
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
          {ORG_STANDARD_REPORTS.map((report) => {
            const Icon = report.icon;
            const isSelected = selectedReportType === report.id;

            return (
              <button
                key={report.id}
                onClick={() => setSelectedReportType(report.id)}
                className={`p-3 rounded-lg border transition-all text-left ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-border-light dark:border-border-dark hover:border-primary-300 dark:hover:border-primary-600 bg-white dark:bg-background-dark-secondary'
                }`}
                title={report.description}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-1.5 rounded-lg ${report.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-medium text-text-primary dark:text-text-dark-primary text-center leading-tight">
                    {report.tileLabel || report.title}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected Report Display */}
        {selectedReportType && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
                {ORG_STANDARD_REPORTS.find((r) => r.id === selectedReportType)?.title}
              </h3>
            </div>

            {/* Share URL for standard (non-SOS) reports */}
            {isShareableStandardReport && (
              <ShareUrlDisplay
                reportType={selectedReportType}
                organizationId={organizationId}
              />
            )}

            {/* Report Component */}
            <div className="card">
              {selectedReportType === 'briefs' && (
                <BriefsReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                  organizationLogoUrl={organizationLogoUrl ?? undefined}
                />
              )}
              {selectedReportType === 'hot-bills' && (
                <HotBillsReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'bill-tracking' && (
                <BillTrackingReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'legislators' && (
                <LegislatorsReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'candidates' && (
                <CandidatesReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'ballot-initiatives' && (
                <BallotInitiativesReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'legislator-votes-on-tracked-bills' && (
                <LegislatorVotesReportWrapper
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {selectedReportType === 'scorecard' && (
                <ScorecardReport
                  organizationId={organizationId}
                  organizationName={organizationName}
                />
              )}
              {isSosReport && (
                <OrgReportsTab
                  organizationId={organizationId}
                  organizationName={organizationName}
                  organizationLogoUrl={organizationLogoUrl ?? undefined}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgReportsTabContent;
